import { BackLink } from '@/components/BackLink';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { Heading } from '@/components/Heading';
import { Pill } from '@/components/Pill';
import { SkeletonList } from '@/components/Skeleton';
import { SpecBlock, SpecRows, type SpecRow } from '@/components/SpecBlock';
import { F } from '@/design/fonts';
import { SPACE, T } from '@/design/tokens';
import { DocumentImage } from '@/features/vehicles/DocumentImage';
import { useServiceEntry } from '@/hooks/useServiceEntry';
import type { LineItem } from '@/types/domain';
import { format, parseISO } from 'date-fns';
import { useParams } from 'react-router-dom';

/**
 * Format an integer minor-unit amount (e.g. cents) as a currency string.
 * Uses integer arithmetic — never floating-point.
 * e.g. formatMinor(1099, "USD") → "$10.99"
 */
function formatMinor(minor: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(minor / 100);
  } catch {
    // Fallback for unknown currency codes
    const whole = Math.trunc(minor / 100);
    const frac = Math.abs(minor % 100)
      .toString()
      .padStart(2, '0');
    return `${whole}.${frac} ${currency}`;
  }
}

function formatPerformedAt(performedAt: string): string {
  try {
    return format(parseISO(performedAt), 'MMMM d, yyyy');
  } catch {
    return performedAt;
  }
}

// Labels the `source` enum value only. Spec 011 added an orthogonal
// `entry.selfPerformed: boolean` field (self_performed here is the legacy
// pre-spec-011 enum value, not read for new writes). Whoever renders
// "self-performed" for E3 value-invested logic MUST treat
// `source === 'self_performed' || selfPerformed === true` as self-performed
// (lamport) — checking this map alone misses the new field.
const SOURCE_LABELS: Record<string, string> = {
  manual: 'Manual entry',
  shop_invoice: 'Shop invoice',
  self_performed: 'Self-performed',
};

const KIND_LABELS: Record<string, string> = {
  part: 'Part',
  labor: 'Labor',
  fee: 'Fee',
  tax: 'Tax',
  shipping: 'Shipping',
  discount: 'Discount',
  other: 'Other',
};

function LineItemRow({ item, currency }: { item: LineItem; currency?: string }) {
  const itemCurrency = item.currency ?? currency ?? 'USD';
  return (
    <tr>
      <td
        style={{
          padding: `${SPACE.sm}px ${SPACE.md}px ${SPACE.sm}px 0`,
          verticalAlign: 'top',
          color: T.text,
          fontSize: 13,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        <div>{item.description}</div>
        {item.kind ? (
          <div style={{ color: T.textMuted, fontSize: 12, marginTop: SPACE.xs }}>
            {KIND_LABELS[item.kind] ?? item.kind}
            {item.partNumber ? ` · ${item.partNumber}` : ''}
          </div>
        ) : null}
      </td>
      <td
        style={{
          padding: `${SPACE.sm}px ${SPACE.md}px ${SPACE.sm}px`,
          verticalAlign: 'top',
          color: T.textMuted,
          fontSize: 13,
          borderBottom: `1px solid ${T.border}`,
          whiteSpace: 'nowrap',
        }}
      >
        {typeof item.quantity === 'number' ? `×${item.quantity}` : ''}
      </td>
      <td
        style={{
          padding: `${SPACE.sm}px 0 ${SPACE.sm}px ${SPACE.md}px`,
          verticalAlign: 'top',
          color: T.text,
          fontSize: 13,
          borderBottom: `1px solid ${T.border}`,
          textAlign: 'right',
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatMinor(item.amountMinor, itemCurrency)}
      </td>
    </tr>
  );
}

export function ServiceEntryDetail() {
  const { id: vehicleId = '', entryId = '' } = useParams<{ id: string; entryId: string }>();
  const { data: entry, loading, error } = useServiceEntry(vehicleId, entryId);

  return (
    <section>
      <BackLink to={`/vehicles/${vehicleId}/service`}>← service log</BackLink>

      {loading ? (
        <div style={{ marginTop: SPACE.lg }}>
          <SkeletonList count={3} />
        </div>
      ) : error ? (
        <div style={{ marginTop: SPACE.lg }}>
          <Card accent="red">
            <p style={{ margin: 0, fontSize: 14, color: T.text }}>
              Couldn't load this entry: {error.message}
            </p>
          </Card>
        </div>
      ) : !entry ? (
        <div style={{ marginTop: SPACE.lg }}>
          <EmptyState>Entry not found.</EmptyState>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: SPACE.lg, marginTop: SPACE.lg }}>
          {/* ---------------------------------------------------------------- */}
          {/* Block A — the entry record (identity zone + ruled field rows)    */}
          {/* ---------------------------------------------------------------- */}
          <SpecBlock>
            {/* Identity zone */}
            <div
              style={{
                padding: SPACE.md,
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: SPACE.sm,
                flexWrap: 'wrap',
              }}
            >
              {/* Left: title + date subtitle */}
              <div>
                <Heading level={1} size="xl">
                  {entry.summary}
                </Heading>
                <p
                  style={{
                    color: T.textMuted,
                    fontFamily: F.body,
                    margin: `${SPACE.xs}px 0 0`,
                  }}
                >
                  {formatPerformedAt(entry.performedAt)}
                </p>
              </div>
              {/* Right: source as identity-zone neutral pill (when present) */}
              {entry.source ? (
                <div
                  style={{
                    display: 'flex',
                    gap: SPACE.xs,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  <Pill variant="neutral">{SOURCE_LABELS[entry.source] ?? entry.source}</Pill>
                </div>
              ) : null}
            </div>

            {/* Ruled field rows — present-only, in canonical order, last row no border-bottom */}
            {(() => {
              const rows: SpecRow[] = [];

              if (typeof entry.mileage === 'number') {
                rows.push({
                  label: 'Mileage',
                  value: `${entry.mileage.toLocaleString()} mi`,
                  mono: true,
                });
              }
              if (typeof entry.laborHours === 'number') {
                rows.push({ label: 'Labor hours', value: `${entry.laborHours} h`, mono: true });
              }
              if (entry.vendor) {
                rows.push({ label: 'Vendor', value: entry.vendor, mono: false });
              }
              if (entry.performedBy) {
                rows.push({ label: 'Performed by', value: entry.performedBy, mono: false });
              }
              if (Array.isArray(entry.systems) && entry.systems.length > 0) {
                rows.push({
                  label: 'Systems',
                  value: (
                    <div style={{ display: 'flex', gap: SPACE.sm, flexWrap: 'wrap' }}>
                      {entry.systems.map((tag, i) => (
                        <Pill key={i} variant="neutral">
                          {tag}
                        </Pill>
                      ))}
                    </div>
                  ),
                  mono: false,
                  align: 'center',
                });
              }

              return <SpecRows rows={rows} labelCol="110px" />;
            })()}
          </SpecBlock>

          {/* ---------------------------------------------------------------- */}
          {/* Block B — line items (own ruled block, only when present)        */}
          {/* ---------------------------------------------------------------- */}
          {Array.isArray(entry.lineItems) && entry.lineItems.length > 0 ? (
            <SpecBlock>
              {/* Section-label zone */}
              <div style={{ padding: SPACE.md }}>
                <Heading level={2} size="md">
                  Line items
                </Heading>
              </div>
              {/* Table region — opens with borderTop mirroring Block A's rows region */}
              <div style={{ borderTop: `1px solid ${T.border}`, padding: SPACE.md }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          textAlign: 'left',
                          fontSize: 11,
                          color: T.textMuted,
                          letterSpacing: '0.04em',
                          fontWeight: 600,
                          paddingBottom: SPACE.sm,
                          borderBottom: `1px solid ${T.border}`,
                        }}
                      >
                        DESCRIPTION
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          fontSize: 11,
                          color: T.textMuted,
                          letterSpacing: '0.04em',
                          fontWeight: 600,
                          paddingBottom: SPACE.sm,
                          paddingLeft: SPACE.md,
                          borderBottom: `1px solid ${T.border}`,
                        }}
                      >
                        QTY
                      </th>
                      <th
                        style={{
                          textAlign: 'right',
                          fontSize: 11,
                          color: T.textMuted,
                          letterSpacing: '0.04em',
                          fontWeight: 600,
                          paddingBottom: SPACE.sm,
                          paddingLeft: SPACE.md,
                          borderBottom: `1px solid ${T.border}`,
                        }}
                      >
                        AMOUNT
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {entry.lineItems.map((item, idx) => (
                      <LineItemRow
                        key={idx}
                        item={item}
                        {...(entry.currency ? { currency: entry.currency } : {})}
                      />
                    ))}
                  </tbody>
                  {typeof entry.totalMinor === 'number' ? (
                    <tfoot>
                      <tr>
                        <td
                          colSpan={2}
                          style={{
                            paddingTop: SPACE.sm,
                            fontSize: 13,
                            fontWeight: 600,
                            color: T.textMuted,
                          }}
                        >
                          Total
                        </td>
                        <td
                          style={{
                            paddingTop: SPACE.sm,
                            textAlign: 'right',
                            fontSize: 14,
                            fontWeight: 700,
                            color: T.text,
                            whiteSpace: 'nowrap',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {formatMinor(entry.totalMinor, entry.currency ?? 'USD')}
                        </td>
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>
            </SpecBlock>
          ) : null}

          {/* Invoice photo — labeled section, not a Card, not inside a block */}
          {entry.invoiceImageRef ? (
            <div>
              <Heading level={2} size="md" style={{ marginBottom: SPACE.sm }}>
                Invoice photo
              </Heading>
              <DocumentImage vehicleId={vehicleId} imageRef={entry.invoiceImageRef} />
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
